import { isTrustedAuthOrigin } from "@/lib/trusted-auth-origin";
import { useState, useEffect, createContext, useContext, useRef, useCallback } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";

const MAX_REFRESH_RETRIES = 3;
const RETRY_DELAY_MS = 3000;

// Detect at module load (before supabase strips the URL hash) whether this
// tab was spawned by an iframe-breakout SSO sign-in. We require an explicit
// `sso_breakout=1` query marker added by Auth.tsx only when the sign-in
// was initiated from an embedded (iframe) context — otherwise a normal
// standalone tab that happens to have a window.opener (e.g. user opened
// the preview via "Open in new tab") would self-close after sign-in.
const isAuthBreakoutTab =
  typeof window !== "undefined" &&
  !!window.opener &&
  window.opener !== window &&
  /[?&]sso_breakout=1\b/.test(window.location.search) &&
  /access_token=|code=/.test(window.location.hash + window.location.search);

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
});

// Awaited role assignment with error handling and query invalidation
const applyRolesOnLogin = async (userId: string, queryClient: ReturnType<typeof useQueryClient>) => {
  try {
    const { data, error } = await supabase.rpc("apply_pending_role_assignment", { _user_id: userId });
    if (error) {
      console.error("Failed to apply pending role:", error);
    } else {
      console.log("Role assignment result:", data);
    }
  } catch (err) {
    console.error("Role assignment exception:", err);
  }

  try {
    const { error } = await supabase.rpc("auto_assign_driver_role_on_login", { _user_id: userId });
    if (error) {
      console.error("Failed to auto-assign driver role:", error);
    }
  } catch (err) {
    console.error("Driver role assignment exception:", err);
  }

  // Invalidate role/auth queries so UI refreshes
  queryClient.invalidateQueries({ queryKey: ["user-role"] });
  queryClient.invalidateQueries({ queryKey: ["user-authorization"] });
};

// Retry session refresh with exponential back-off style retries
const retrySessionRefresh = async (
  attempt: number,
  onSuccess: (session: Session) => void,
  onFailure: () => void,
) => {
  for (let i = attempt; i <= MAX_REFRESH_RETRIES; i++) {
    console.warn(`[Auth] Token refresh retry ${i}/${MAX_REFRESH_RETRIES}`);
    try {
      const { data, error } = await supabase.auth.refreshSession();
      if (!error && data.session) {
        console.log("[Auth] Token refresh succeeded on retry", i);
        onSuccess(data.session);
        return;
      }
      console.error(`[Auth] Token refresh attempt ${i} failed:`, error?.message);
    } catch (err) {
      console.error(`[Auth] Token refresh attempt ${i} exception:`, err);
    }

    if (i < MAX_REFRESH_RETRIES) {
      await new Promise((r) => setTimeout(r, RETRY_DELAY_MS * i));
    }
  }

  console.error("[Auth] All token refresh retries exhausted — signing out");
  onFailure();
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();
  const roleAssignmentInFlight = useRef<string | null>(null);
  const retryInFlight = useRef(false);

  const handleSessionRecovered = useCallback((recovered: Session) => {
    retryInFlight.current = false;
    setSession(recovered);
    setUser(recovered.user);
  }, []);

  const handleSessionLost = useCallback(() => {
    retryInFlight.current = false;
    setSession(null);
    setUser(null);
    // session_expired cannot be logged securely — JWT is already invalid, auth.uid() is null.
    supabase.auth.signOut().catch(() => {});
  }, []);

  useEffect(() => {
    const tryApplyRoles = (userId: string) => {
      // Single-flight guard: don't fire duplicate concurrent attempts
      if (roleAssignmentInFlight.current === userId) return;
      roleAssignmentInFlight.current = userId;
      applyRolesOnLogin(userId, queryClient).finally(() => {
        roleAssignmentInFlight.current = null;
      });
    };

    // Listen for auth changes — this is the single source of truth.
    // INITIAL_SESSION fires once after any OAuth tokens in the URL are
    // processed, so we no longer call getSession() separately (which
    // can resolve before token exchange completes on mobile redirects).
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === "TOKEN_REFRESHED" && !session) {
          // Token refresh failed — attempt retries before signing out
          if (!retryInFlight.current) {
            retryInFlight.current = true;
            retrySessionRefresh(1, handleSessionRecovered, handleSessionLost);
          }
          return;
        }

        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        
        if ((event === "SIGNED_IN" || event === "INITIAL_SESSION") && session?.user) {
          // Login is now captured by a server-side DB trigger — no client logging needed.
          tryApplyRoles(session.user.id);

          // Iframe-breakout SSO: signal the opener (Lovable preview pane)
          // that auth completed, then close this temporary tab.
          if (isAuthBreakoutTab) {
            try {
              // Forward the session tokens so an iframe-embedded
              // opener (whose localStorage is partitioned away from
              // this top-level tab) can hydrate via setSession.
              window.opener?.postMessage(
                {
                  type: "AUTH_COMPLETE",
                  access_token: session.access_token,
                  refresh_token: session.refresh_token,
                },
                "*",
              );
            } catch (err) {
              console.warn("[Auth] postMessage to opener failed", err);
            }
            // Defer close so postMessage flushes.
            setTimeout(() => window.close(), 50);
          }
        } else if (event === "SIGNED_OUT") {
          // Logout is now captured by a server-side DB trigger on auth.sessions — no client logging needed.
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [queryClient, handleSessionRecovered, handleSessionLost]);

  // When this app is embedded in an iframe (Lovable chat preview),
  // localStorage is partitioned per top-level site, so the SSO popup
  // tab — which runs top-level on lovableproject.com — writes its
  // session into a *different* storage partition than the iframe.
  // The popup forwards the tokens to us here; we hydrate the session
  // directly so the iframe becomes authenticated.
  useEffect(() => {
    const handler = async (e: MessageEvent) => {
      if (!isTrustedAuthOrigin(e.origin)) return;
      const data = e.data;
      if (!data || data.type !== "AUTH_COMPLETE") return;
      if (data.access_token && data.refresh_token) {
        try {
          await supabase.auth.setSession({
            access_token: data.access_token,
            refresh_token: data.refresh_token,
          });
        } catch (err) {
          console.error("[Auth] setSession from opener message failed", err);
        }
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  return (
    <AuthContext.Provider value={{ user, session, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
